// pages/messenger/messenger-jobdetail.jsx

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import RequestService from '../../services/requestService';
import SubServiceTypeService from '../../services/subServiceTypeService';
import { WF } from '../../components/utils/messenger-constants';
import { getEmail, formatDate, getSectionLabel, getDocTypeLabel, ToThaiDateTime } from '../../components/utils/messenger-helpers';
import LoadingDots from '../../components/common/loading-dots';
import MessengerIncompleteModal from '../../components/common/messenger-incomplete-modal';

// ── "งานที่ต้องทำ" ต้องสื่อให้ messenger รู้ว่างานคือประเภทไหน อ้างจาก detail.id_service_type ──
// ── 1 = รับส่งเอกสาร, 2 = ธนาคาร, 3 = ชำระค่าบริการ (ตรงกับ Service Type ในหน้า user) ──
const SERVICE_TYPE = {
    DOCUMENT: 1,
    BANK: 2,
    TRANSACTION: 3,
};

// ── งานเอกสาร: id_sub_service_type = แบบการรับส่ง (delivery_type) ที่ user เลือก ──
const DELIVERY_TYPE_MAP = {
    1: 'รับเท่านั้น',
    2: 'ส่งเท่านั้น',
    3: 'ส่งและรับกลับ',
};

// ── รอบส่ง "ด่วน" (Urgent) ── ต่อท้าย label งานเอกสารด้วย " - ด่วน" ──
const DELIVERY_SECTION_URGENT = 3;

// ── งานชำระค่าบริการ: ชื่อประเภทย่อย (ค่าน้ำ/ค่าไฟ/ค่าโทรศัพท์) อยู่ใน master m_sub_service_type ──
// ── backend ส่ง detail มาแค่ id_sub_service_type (ตัวเลข) จึงต้อง lookup ชื่อจาก map ที่ fetch มา ──
const getWorkLabel = (job, detail, subServiceMap = {}) => {
    switch (Number(detail?.id_service_type)) {
        case SERVICE_TYPE.DOCUMENT: {
            const deliveryLabel = DELIVERY_TYPE_MAP[Number(detail?.id_sub_service_type)] || 'รับส่งเอกสาร';
            // ── รอบส่งด่วน → ต่อท้าย " - ด่วน" เช่น "รับเท่านั้น - ด่วน" ──
            return Number(job?.delivery_section) === DELIVERY_SECTION_URGENT
                ? `${deliveryLabel} - ด่วน`
                : deliveryLabel;
        }

        case SERVICE_TYPE.BANK:
            // ── ชื่อธนาคารโชว์ในช่อง "ติดต่อผู้รับ" อยู่แล้ว ตรงนี้บอกแค่ว่าเป็นงานธนาคาร ──
            return 'ทำธุรกรรมธนาคาร';

        case SERVICE_TYPE.TRANSACTION: {
            const subName = subServiceMap[Number(detail?.id_sub_service_type)];
            return subName ? `ชำระค่าบริการ (${subName})` : 'ชำระค่าบริการ';
        }

        default:
            return getDocTypeLabel(job?.document_type);
    }
};

// ── ชื่อสาขา (Service Type > สาขา ฝั่ง user) สำหรับงานธนาคาร/ชำระค่าบริการ ──
// ── สาขาปกติ backend expand relation destination_provider_branch มาให้ ──
// ── เคส "อื่นๆ" ที่ user พิมพ์เอง จะไม่มี branch relation แต่ชื่อไปเก็บใน destination_name ──
const getBranchName = (detail) =>
    detail?.destination_provider_branch?.name_th
    || detail?.destination_provider_branch?.name_en
    || detail?.destination_name
    || '';

// ── งานธนาคาร (2) / ชำระค่าบริการ (3) เท่านั้นที่มีแนวคิด "สาขา" ──
const isProviderService = (detail) =>
    [SERVICE_TYPE.BANK, SERVICE_TYPE.TRANSACTION].includes(Number(detail?.id_service_type));

// ── ชื่อผู้ให้บริการ (ธนาคาร/ผู้รับชำระ) สำหรับงาน 2/3 ──
const getProviderName = (detail) =>
    detail?.destination_provider?.name_th
    || detail?.destination_provider?.name_en
    || detail?.destination_company
    || '';

// ── ปลายทางที่โชว์ในการ์ด "สถานที่จัดส่ง" ──
// ── งานธนาคาร/ชำระบริการ: ชื่อผู้ให้บริการ + สาขา (บอก messenger ว่าไปที่ไหน) ──
// ── งานเอกสาร: บริษัท/ชื่อปลายทางตามเดิม ──
const getDestinationName = (detail) => {
    if (isProviderService(detail)) {
        const provider = getProviderName(detail);
        const branch   = getBranchName(detail);
        return [provider, branch].filter(Boolean).join(' - ') || '-';
    }
    return detail?.destination_company || detail?.destination_name || '-';
};

// ── complete_time_type = 2 (ระบุเวลา) เท่านั้นที่ user กรอกเวลาเสร็จมา ──
const COMPLETE_TIME_TYPE_SPECIFIC = 2;

// ── ดึง HH:mm จาก complete_time_specific ("0000-01-01THH:mm:ssZ") ด้วย regex ──
// ── ⚠️ ห้าม parse ด้วย new Date() เพราะ timezone จะทำให้เวลาเพี้ยน (บั๊กเดียวกับฝั่ง user) ──
const extractCompleteTime = (dateStr) => {
    if (!dateStr) return '';
    const match = String(dateStr).match(/T(\d{2}:\d{2})/);
    return match ? match[1] : '';
};

// ── รอบส่ง + เวลา (ถ้า user ระบุเวลาเสร็จ) เช่น "รอบเช้า 14:30 น." ──
const getSectionTimeLabel = (job, detail) => {
    const sectionLabel = getSectionLabel(job?.delivery_section);
    if (Number(detail?.complete_time_type) === COMPLETE_TIME_TYPE_SPECIFIC) {
        const time = extractCompleteTime(detail?.complete_time_specific);
        if (time) return `${sectionLabel} ${time} น.`;
    }
    return sectionLabel;
};

const MessengerJobDetail = () => {
    const navigate                            = useNavigate();
    const { id: encodedId, seq: encodedSeq } = useParams();
    const id                                  = atob(encodedId);
    const seq                                 = encodedSeq ? Number(atob(encodedSeq)) : 1;

    const [job, setJob]                       = useState(null);
    const [loading, setLoading]               = useState(true);
    const [actionLoading, setActionLoading]   = useState(false);
    const [actionText, setActionText]         = useState('');
    const [error, setError]                   = useState(null);
    const [showIncomplete, setShowIncomplete] = useState(false);
    const [incompleteSubmitted, setIncompleteSubmitted] = useState(false);
    // ── map id_sub_service_type → ชื่อ (ค่าน้ำ/ค่าไฟ/ค่าโทรศัพท์) สำหรับงานชำระค่าบริการ ──
    const [subServiceMap, setSubServiceMap] = useState({});

    const fetchJob = async () => {
        setLoading(true);
        try {
            const resp = await RequestService.GetRequestById(id);
            if (resp?.data?.is_success === true) {
                const jobData = resp.data.result;

                if (jobData.id_workflow_status === WF.USER_CANCEL || jobData.id_workflow_status === WF.ADMIN_CANCEL) {
                    navigate(
                        `/messenger/job-cancelled/${btoa(String(jobData.id))}/${btoa(jobData.ref_no)}`,
                        { replace: true }
                    );
                    return;
                }

                setJob(jobData);
            } else {
                setError('ไม่พบข้อมูลงาน');
            }
        } catch (err) {
            console.error(err);
            setError('เกิดข้อผิดพลาด');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchJob();
    }, [id]);

    // ── โหลด master m_sub_service_type ของงานชำระค่าบริการ (id_service_type = 3) มา build map id → name ──
    useEffect(() => {
        const fetchSubServiceTypes = async () => {
            try {
                const resp = await SubServiceTypeService.SearchSubServiceType({
                    id_service_type: SERVICE_TYPE.TRANSACTION,
                });
                if (resp?.data?.is_success === true) {
                    const map = (resp.data.result || []).reduce((acc, item) => {
                        acc[Number(item.id)] = item.name;
                        return acc;
                    }, {});
                    setSubServiceMap(map);
                }
            } catch (err) {
                console.error('fetchSubServiceTypes error:', err);
            }
        };
        fetchSubServiceTypes();
    }, []);

    // ── เริ่มส่งงาน: POST /requests/{id}/task/start ──
    const handleStartTask = async () => {
        setActionText('เริ่มส่งงาน...');
        setActionLoading(true);
        try {
            const resp = await RequestService.StartTask(id, {
                send_start_location: '',
            });
            if (resp?.data?.is_success === true) {
                await fetchJob();
            }
        } catch (err) {
            console.error(err);
        } finally {
            setActionLoading(false);
        }
    };

    // ── ส่งสำเร็จ: POST /requests/{id}/task/{idTask}/complete ──
    const handleCompleteTask = async (detail) => {
        setActionText('กำลังบันทึก...');
        setActionLoading(true);
        try {
            const details    = job?.request_details || [];
            const totalStops = details.length;

            const resp = await RequestService.CompleteTask(id, detail.id, {
                send_end_location: '',
                note: '',
            });

            if (resp?.data?.is_success === true) {
                navigate(
                    `/messenger/job-done/${btoa(id)}/${btoa(String(seq))}/${btoa(String(totalStops))}`,
                    { replace: true }
                );
                return;
            }
        } catch (err) {
            console.error(err);
        } finally {
            setActionLoading(false);
        }
    };

    if (actionLoading) return <LoadingDots text={actionText} />;
    if (loading)       return <LoadingDots />;

    if (showIncomplete) return (
        <MessengerIncompleteModal
            submitted={incompleteSubmitted}
            onConfirm={async (note) => {
                setActionText('กำลังบันทึก...');
                setActionLoading(true);
                try {
                    const detail = (job?.request_details || []).find(d => d.seq === seq) || job?.request_details?.[0];
                    const resp = await RequestService.CloseTask(id, detail.id, {
                        id_close_reason: 6, // TODO: ใช้จากหน้า select reason จริง
                        send_end_location: '',
                        ...(note && { note }),
                    });
                    if (resp?.data?.is_success === true) {
                        setIncompleteSubmitted(true);
                    }
                } catch (err) {
                    console.error(err);
                } finally {
                    setActionLoading(false);
                }
            }}
            onBack={() => {
                setShowIncomplete(false);
                setIncompleteSubmitted(false);
                navigate('/messenger/myjob', { replace: true });
            }}
        />
    );

    if (error) return (
        <div className="mjd-root">
            <div className="mjd-header">
                <button className="mjd-back-btn" onClick={() => navigate(-1)}>
                    <i className="mdi mdi-arrow-left" />
                </button>
                <h1 className="mjd-header-title">รายละเอียดงาน</h1>
            </div>
            <div className="mjd-error">{error}</div>
        </div>
    );

    const details    = job?.request_details || [];
    const detail     = details.find(d => d.seq === seq) || details[0] || {};
    const totalStops = details.length;
    const wfStatus   = job?.id_workflow_status;

    // console.log('detail:', JSON.stringify(detail));

    // ── สถานะอ้างจาก send_start_date / send_end_date / is_complete ──
    // รอจัดส่ง  → send_start_date, send_end_date เป็น null
    // กำลังส่ง  → มีค่า send_start_date, ยังไม่มี send_end_date
    // ส่งแล้ว   → มีค่าทั้งสองช่อง, is_complete = 1
    // จบงาน     → มีค่าทั้งสองช่อง, is_complete = 0
    const hasStarted  = !!detail.send_start_date;
    const hasEnded    = !!detail.send_end_date;
    const isStopDone  = detail.is_complete == 1;

    const isReassigned = wfStatus === WF.REASSIGN && !hasStarted;
    const isPending     = !hasStarted && !hasEnded;        // รอจัดส่ง
    const isInProgress  = hasStarted && !hasEnded;          // กำลังส่ง
    const isClosed       = hasEnded && !isStopDone;         // จบงาน (is_complete=0)
    const isCancel       = wfStatus === WF.USER_CANCEL || wfStatus === WF.ADMIN_CANCEL;
    const isComplete    = hasEnded && isStopDone;           // ส่งแล้ว (is_complete=1)
    const mapUrl       = detail.destination_map || detail.destination_building?.map || '';

    const getBannerClass = () => {
        if (isCancel)     return 'cancel';
        if (isComplete)   return 'complete';
        if (isClosed)     return 'closed';
        if (isInProgress) return 'inprogress';
        if (isReassigned) return 'pending';
        return 'pending';
    };

    const getBannerIcon = () => {
        if (isCancel)     return <i className="mdi mdi-close-circle" />;
        if (isComplete)   return <i className="mdi mdi-check-circle" />;
        if (isClosed)     return <i className="ri ri-flag-2-fill" />;
        if (isInProgress) return <i className="mdi mdi-moped" />;
        return <i className="mdi mdi-clock" />;
    };

    const getBannerText = () => {
        if (isCancel)     return 'ยกเลิก';
        if (isComplete)   return 'ส่งสำเร็จ';
        if (isClosed)     return 'จบงาน';
        if (isInProgress) return 'กำลังส่ง';
        return 'รอจัดส่ง';
    };

    const renderActions = () => {
        if (!job) return null;
        const wf = job.id_workflow_status;

        // ── ถ้า task นี้เสร็จไปแล้ว (ไม่ว่าสำเร็จหรือจบงาน) ไม่ต้องมีปุ่ม ──
        if (hasEnded) return null;

        const pendingStops   = details.filter(d => d.is_complete != 1 && !d.send_end_date).sort((a, b) => a.seq - b.seq);
        const currentTurnSeq = pendingStops[0]?.seq;
        const isMyTurn        = seq === currentTurnSeq;

        // ── ยังไม่เริ่ม (ไม่มี send_start_date) ──
        if (!hasStarted) {
            if (!isMyTurn) return null;
            return (
                <button
                    className="mjd-btn mjd-btn--primary"
                    onClick={handleStartTask}
                >
                    เริ่มส่งงาน
                </button>
            );
        }

        // ── เริ่มแล้ว รอกดส่งสำเร็จ/ขอจบงาน ──
        if (hasStarted && !hasEnded) {
            return (
                <>
                    <button
                        className="mjd-btn mjd-btn--success"
                        onClick={() => handleCompleteTask(detail)}
                    >
                        ส่งสำเร็จ
                    </button>
                    <button
                        className="mjd-btn mjd-btn--outline"
                        onClick={() => navigate(
                            `/messenger/job-incomplete/${btoa(id)}/${btoa(String(seq))}/${btoa(String(totalStops))}`
                        )}
                    >
                        ขอจบงาน
                    </button>
                </>
            );
        }

        return null;
    };

    const actions = renderActions();

    return (
        <div className="mjd-root">
            <div className="mjd-header">
                <button className="mjd-back-btn" onClick={() => navigate(-1)}>
                    <i className="mdi mdi-arrow-left" />
                </button>
                <h1 className="mjd-header-title">
                    {job?.ref_no}{totalStops > 1 ? `-${seq}` : ''}
                </h1>
            </div>

            <div className="mjd-content">
                <div className={`mjd-status-banner mjd-status-banner--${getBannerClass()}`}>
                    {getBannerIcon()}
                    <span>{getBannerText()}</span>
                </div>

                {hasEnded && ToThaiDateTime(detail.send_end_date) && (
                    <div className="mjd-card mjd-finish-time">
                        <span className="mjd-label">เวลาเสร็จงาน</span>
                        <span className="mjd-finish-time-value">{ToThaiDateTime(detail.send_end_date)}</span>
                    </div>
                )}

                {totalStops > 1 && (
                    <div className="mjd-progress">
                        {details.map((_, i) => (
                            <div
                                key={i}
                                className={`mjd-progress-dot ${
                                    i + 1 === seq ? 'mjd-progress-dot--active' :
                                    i + 1 < seq   ? 'mjd-progress-dot--done'   : ''
                                }`}
                            />
                        ))}
                    </div>
                )}

                <div className="mjd-card">
                    <p className="mjd-ref">Ref No. {job?.ref_no}{totalStops > 1 ? `-${seq}` : ''}</p>
                    <div className="mjd-card-field">
                        <span className="mjd-label">งานที่ต้องทำ</span>
                        <span className="mjd-value">{getWorkLabel(job, detail, subServiceMap)}</span>
                    </div>
                    <div className="mjd-row" style={{ marginTop: 8 }}>
                        <div className="mjd-col">
                            <span className="mjd-label">วันที่ต้องเสร็จงาน</span>
                            <span className="mjd-value">{formatDate(job?.service_date)}</span>
                        </div>
                        <div className="mjd-col">
                            <span className="mjd-label">รอบส่ง</span>
                            <span className="mjd-value">{getSectionTimeLabel(job, detail)}</span>
                        </div>
                    </div>
                </div>

                <div className="mjd-card">
                    <p className="mjd-section-title">สถานที่จัดส่ง</p>
                    <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 0 }}>
                        <div className="mjd-location">
                            <div className="mjd-location-line-wrap">
                                <i className="mdi mdi-record-circle mjd-location-dot--from" />
                                <div className="mjd-location-line" />
                            </div>
                            <div className="mjd-location-body" style={{ paddingBottom: 16 }}>
                                <span className="mjd-label">ต้นทาง</span>
                                <span className="mjd-value">{detail.origin_building_name || '-'}</span>
                            </div>
                        </div>
                        <div className="mjd-location">
                            <div className="mjd-location-line-wrap">
                                <i className="ri ri-map-pin-2-fill mjd-location-dot--to" />
                            </div>
                            <div className="mjd-location-body">
                                <span className="mjd-label">ปลายทาง</span>
                                <span className="mjd-value">
                                    {getDestinationName(detail)}
                                    {!isProviderService(detail) && detail.destination_floor ? ` ชั้น ${detail.destination_floor}` : ''}
                                    {!isProviderService(detail) && detail.destination_department ? ` แผนก ${detail.destination_department}` : ''}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {mapUrl && (
                    <div className="mjd-card-map">
                        <span className="mjd-label">Google Map</span>
                        <a href={mapUrl} target="_blank" rel="noreferrer" className="mjd-map-link">
                            {mapUrl}
                        </a>
                    </div>
                )}

                <div className="mjd-card">
                    <div className="mjd-contact-icon mjd-contact-icon--receiver">
                        <i className="mdi mdi-account-multiple" />
                        <span className="mjd-contact-type">ติดต่อผู้รับ</span>
                    </div>
                    <div className="mjd-contact-body">
                        <span className="mjd-label">ชื่อ-สกุล</span>
                        <span className="mjd-value">
                            {isProviderService(detail)
                                ? getDestinationName(detail)
                                : (detail.destination_contact_name || '-')}
                        </span>
                        <span className="mjd-label">โทร</span>
                        <span className="mjd-value">{detail.destination_phone_number || '-'}</span>
                        {detail.note && (
                            <>
                                <span className="mjd-label">หมายเหตุ</span>
                                <span className="mjd-value">{detail.note}</span>
                            </>
                        )}
                    </div>
                </div>

                <div className="mjd-card">
                    <div className="mjd-contact-icon mjd-contact-icon--admin">
                        <i className="mdi mdi-message-alert" />
                        <span className="mjd-contact-type">เจ้าของงาน</span>
                    </div>
                    <div className="mjd-contact-body">
                        <span className="mjd-label">ชื่อ-สกุล</span>
                        <span className="mjd-value">{job?.sender_name || '-'}</span>
                        <span className="mjd-label">บริษัท</span>
                        <span className="mjd-value">{job?.sender_business_unit?.name_th || job?.sender_business_unit?.name_en || '-'}</span>
                        <span className="mjd-label">โทร</span>
                        <span className="mjd-value">{job?.sender_tel || '-'}</span>
                    </div>
                </div>
            </div>

            {actions && (
                <div className="mjd-footer">
                    {actions}
                </div>
            )}
        </div>
    );
};

export default MessengerJobDetail;